import type { QueryClient } from '@tanstack/react-query';
import { isVitest } from '@/lib/test-env';
import { getUrlMetadataClient } from '@/lib/url-metadata/trpc-client';
import { URL_SCHEME_PREFIX_PATTERN, WWW_PREFIX_PATTERN } from '@/lib/utils/regex';
import { FetchUrlMetadataResultSchema, parseLinkMetadata } from './schemas';
import type { LinkMetadata } from './types';

type FetchUrlMetadataMutation = {
  mutate: (input: { strategy: 'cache'; urls: string[] }) => Promise<unknown>;
};

const ABSOLUTE_URL_PATTERN = URL_SCHEME_PREFIX_PATTERN;
const PLACEHOLDER_URL_BASE = 'https://placeholder.invalid';

const isAbsoluteUrl = (value: string): boolean => ABSOLUTE_URL_PATTERN.test(value.trim());

const parseAbsoluteUrl = (value: string): URL => {
  if (!isAbsoluteUrl(value)) {
    throw new Error('URL must be absolute');
  }
  return new URL(value, PLACEHOLDER_URL_BASE);
};

class UrlMetadataBatchLoader {
  private readonly queue = new Set<string>();
  private readonly resolvers = new Map<
    string,
    { resolve: (value: LinkMetadata | null) => void; reject: (error: unknown) => void }
  >();
  private timer: ReturnType<typeof setTimeout> | null = null;

  load(url: string): Promise<LinkMetadata | null> {
    return new Promise((resolve, reject) => {
      this.queue.add(url);
      this.resolvers.set(url, { resolve, reject });

      if (!this.timer) {
        const delay = isVitest() ? 0 : 10;
        this.timer = setTimeout(() => {
          this.flush().catch(() => null);
        }, delay);
      }
    });
  }

  private async flush() {
    const urls = [...this.queue];
    const resolvers = new Map(this.resolvers);

    this.queue.clear();
    this.resolvers.clear();
    this.timer = null;

    if (urls.length === 0) {
      return;
    }

    try {
      const timeoutMs = isVitest() ? 100 : 3000;
      const timeoutPromise = new Promise<'__timeout__'>((resolve) => {
        setTimeout(() => {
          resolve('__timeout__');
        }, timeoutMs);
      });

      type MetadataFetchResponse = {
        metadata: Map<string, LinkMetadata>;
        errors: Map<string, string>;
      };

      const networkPromise: Promise<MetadataFetchResponse> = (async () => {
        const trpcClient = getUrlMetadataClient();
        const fetchUrlMetadata = getFetchUrlMetadataMutation(trpcClient);
        const resultRaw = await fetchUrlMetadata.mutate({
          strategy: 'cache',
          urls,
        });
        const result = FetchUrlMetadataResultSchema.parse(resultRaw ?? {});

        const metadataEntries = Object.entries(result.metadata ?? {}).flatMap(([entryUrl, value]) => {
          const parsed = parseLinkMetadata(value);
          return parsed ? ([[entryUrl, parsed]] as const) : [];
        });
        const metadata = new Map<string, LinkMetadata>(metadataEntries);

        const errorEntries = Object.entries(result.errors ?? {}).filter(
          (entry): entry is [string, string] => typeof entry[1] === 'string' && entry[1].length > 0
        );
        const errors = new Map<string, string>(errorEntries);

        return { metadata, errors };
      })();

      const raced = await Promise.race<MetadataFetchResponse | '__timeout__'>([networkPromise, timeoutPromise]);

      if (raced === '__timeout__') {
        for (const url of urls) {
          resolvers.get(url)?.resolve(null);
        }
        return;
      }

      const response = raced;

      for (const url of urls) {
        const resolver = resolvers.get(url);
        if (!resolver) continue;

        if (response.metadata.has(url)) {
          resolver.resolve(response.metadata.get(url) ?? null);
        } else if (response.errors.has(url)) {
          const errorMessage = response.errors.get(url) ?? 'Unknown metadata error';
          resolver.reject(new Error(errorMessage));
        } else {
          resolver.resolve(null);
        }
      }
    } catch (error) {
      for (const url of urls) {
        const resolver = resolvers.get(url);
        if (resolver) {
          resolver.reject(error);
        }
      }
    }
  }
}

export const batchLoader = new UrlMetadataBatchLoader();

const PLACEHOLDER_DESCRIPTION = 'Link preview unavailable';
const PLACEHOLDER_TITLE_PREFIX = 'content from ';

export function isPlaceholderMetadata(metadata: LinkMetadata | null | undefined, targetUrl: string): boolean {
  if (!metadata) {
    return false;
  }

  const normalizedTitle = typeof metadata.title === 'string' ? metadata.title.trim().toLowerCase() : '';
  const normalizedDescription = typeof metadata.description === 'string' ? metadata.description.trim() : '';

  const titleLooksGeneric = normalizedTitle.length === 0 || normalizedTitle.startsWith(PLACEHOLDER_TITLE_PREFIX);
  const descriptionLooksGeneric =
    normalizedDescription.length === 0 || normalizedDescription === PLACEHOLDER_DESCRIPTION;

  if (!(titleLooksGeneric || descriptionLooksGeneric)) {
    return false;
  }

  try {
    const targetHost = parseAbsoluteUrl(targetUrl).hostname.replace(WWW_PREFIX_PATTERN, '').toLowerCase();
    const siteName = typeof metadata.siteName === 'string' ? metadata.siteName.trim().toLowerCase() : undefined;
    const hostMatchesSiteName = !siteName || siteName === targetHost;

    const metadataHost =
      typeof metadata.url === 'string'
        ? parseAbsoluteUrl(metadata.url).hostname.replace(WWW_PREFIX_PATTERN, '').toLowerCase()
        : undefined;
    const hostMatchesUrl = !metadataHost || metadataHost === targetHost;

    return hostMatchesSiteName && hostMatchesUrl && titleLooksGeneric && descriptionLooksGeneric;
  } catch {
    return titleLooksGeneric && descriptionLooksGeneric;
  }
}

export const populateUrlMetadataCache = (queryClient: QueryClient, metadata: Record<string, LinkMetadata>) => {
  for (const [url, meta] of Object.entries(metadata)) {
    queryClient.setQueryData(['url-metadata', url], meta);
  }
};

const getFetchUrlMetadataMutation = (client: ReturnType<typeof getUrlMetadataClient>): FetchUrlMetadataMutation => {
  const utilities = client.utilities;
  if (!utilities?.fetchUrlMetadata) {
    throw new Error('tRPC utilities.fetchUrlMetadata mutation unavailable');
  }
  return utilities.fetchUrlMetadata;
};
